import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Grid as MuiGrid } from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { useAppDispatch, useAppSelector } from '../../../hooks/'
import SelectPlan from '../../components/Plan/SelectPlan'
import UserResourcesManagementSelectionGallery from '../../components/UserResourcesManagementSelectionGallery'
import HomeCard from '../../components/HomeCard'
import ErrorBoundary, { LocationTag } from '../../components/ErrorBoundary'
import { shouldLoadData } from '../../hooks/useDataLoader'
import { getVehicles } from '../../../actions/vehicles'

import { Text } from '../../../components'
import AccountCard from '@/app/features/settings/AccountCard'
import ManageProfileCard from '@/app/features/settings/ManageProfileCard'
import { updateRouteOnMobile } from '@/utils/messageUtils'
import SupportCard from 'features/settings/SupportCard/SupportCard'
import ProfileUtilityCard from '@/authenticated/pages/Profile/UtilityCard/ProfileUtilityCard'
import NotificationsCard from '@/app/features/settings/NotificationsCard'
import { isNativeMobile } from '@/authenticated/hooks/useIsMobile'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'

const Grid = styled(MuiGrid)(spacing)

export default function Profile() {
  const dispatch = useAppDispatch()
  const isEditingPlan = useAppSelector(
    (state) => state.manualPlans.isEditingPlan,
  )
  const vehiclesAreLoading = useAppSelector((state) => state.vehicles.isLoading)
  const vehiclesErrors = useAppSelector((state) => state.vehicles.errors)

  const isMobile = isNativeMobile()

  const pushNotificationSettingsFeatureFlagEnabled = useFeatureFlag({
    flag: 'push_notification_settings',
    experimentalValue: true,
  })

  useEffect(() => {
    if (shouldLoadData(false, vehiclesAreLoading, vehiclesErrors)) {
      dispatch(getVehicles())
    }

    // needed for android since route events don't always get triggered correctly
    updateRouteOnMobile('/settings')
  }, [])

  const columnClassName = 'md:max-w-[335px] flex-1 flex flex-col gap-4'

  const leftColumn = (
    <div className={columnClassName}>
      <ErrorBoundary
        location={LocationTag.UtilityProviderCard}
        functionalityDescription="Utility Provider Controls"
        fallbackOnCard
      >
        <ProfileUtilityCard />
      </ErrorBoundary>
    </div>
  )

  const rightColumn = (
    <div className={columnClassName}>
      <ErrorBoundary
        location={LocationTag.HomeCard}
        functionalityDescription="Home Controls"
        fallbackOnCard
      >
        <HomeCard />
      </ErrorBoundary>
      {isMobile && pushNotificationSettingsFeatureFlagEnabled && (
        <NotificationsCard />
      )}
      <SupportCard />
      <ManageProfileCard />
      <AccountCard />
    </div>
  )

  if (isEditingPlan) {
    return (
      <Grid
        justifyContent="center"
        alignItems="center"
        container
        p={{ xs: 5, lg: 10 }}
      >
        <Grid item xs={12} md={8} lg={8} xl={4}>
          <SelectPlan cancelOption={true} />
        </Grid>
      </Grid>
    )
  }

  return (
    <div className="w-full flex justify-center py-[24px] px-5 lg:px-0">
      <div className="flex flex-col w-full max-w-desktop">
        <Text variant="h2" className="mb-6">
          Profile
        </Text>
        <ErrorBoundary
          location={LocationTag.UserResourcesManagementSelectionGallery}
          functionalityDescription="Device Gallery"
        >
          <UserResourcesManagementSelectionGallery />
        </ErrorBoundary>
        <div className="flex flex-col md:flex-row justify-between w-full mt-4 md:gap-6 gap-4">
          {leftColumn}
          {rightColumn}
        </div>
      </div>
    </div>
  )
}
